import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Fragment } from "react";
import { Button, Textarea, Input } from "components/ui";
import { Listbox } from "components/shared/form/Listbox";
import { getStageAndSubStages } from "services/leadStageService";
import { getStageFieldsBySubStageId, upsertStageFieldOption, deleteStageFieldOption, getLeadFieldValues } from "services/stageFieldService";
import { getFieldTypes } from "services/formBuilderService";
import CustomCreatableDropdown from "custom-components/shared/CustomCreatableDropdown";
import { toast } from "sonner";
import { isClosedStage, isJobStage } from "utils/leadStageHelpers";

const StageChangeModal = ({ isOpen, onClose, leadData, onStageChange }) => {
  const [formData, setFormData] = useState({
    leadStage: "",
    leadStageId: "",
    subStage: "",
    subStageId: "",
    notes: "",
    leadScore: "",
  });
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Custom Fields State
  const [customFields, setCustomFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});
  const [initialFieldValues, setInitialFieldValues] = useState({});
  const [fieldsLoading, setFieldsLoading] = useState(false);
  const [dropdownTypeId, setDropdownTypeId] = useState(7);

  const saveRef = useRef(null);

  // Fetch active field types once on mount to dynamically resolve dropdown type ID
  useEffect(() => {
    const loadTypes = async () => {
      try {
        const res = await getFieldTypes();
        const raw = res?.data ?? res;
        if (Array.isArray(raw)) {
          const dropdownType = raw.find(
            (ft) => ft.typeName?.toLowerCase() === "dropdown" || ft.value?.toLowerCase() === "select" || ft.label?.toLowerCase() === "dropdown"
          );
          if (dropdownType) {
            setDropdownTypeId(dropdownType.fieldTypeId ?? dropdownType.id);
          }
        }
      } catch (err) {
        console.error("Failed to load field types in modal", err);
      }
    };
    loadTypes();
  }, []);

  // Load stages when modal opens
  useEffect(() => {
    if (isOpen) {
      resetForm();
      loadStages();
    } else {
      resetForm();
    }
  }, [isOpen, leadData]);

  // Initialize form with leadData stage/substage names after stages are loaded
  useEffect(() => {
    if (isOpen && stages.length > 0 && (leadData?.stage || leadData?.stageConstant || leadData?.StageConstant)) {
      const currentStage = stages.find(s =>
        s.stageName === leadData.stage ||
        (leadData.StageConstant && s.stageConstant?.toLowerCase() === leadData.StageConstant?.toLowerCase())
      );


      if (currentStage) {
        // Only initialize if form is empty or significantly different from leadData
        const shouldInitialize = !formData.leadStageId ||
          formData.leadStage !== currentStage.stageName ||
          (leadData.subStage && formData.subStage !== leadData.subStage);

        if (shouldInitialize) {
          const isContractWonLost = isClosedStage(currentStage.stageConstant);
          let initialSubStage = "";
          let initialSubStageId = "";

          if (isContractWonLost && currentStage.subStages) {
            const winSub = currentStage.subStages.find(
              s => s.isActive && (
                s.subStageName?.toLowerCase().includes("win") ||
                s.subStageName?.toLowerCase().includes("won") ||
                s.subStageConstant?.toLowerCase() === "win" ||
                s.subStageConstant?.toLowerCase() === "won"
              )
            );
            if (winSub) {
              initialSubStage = winSub.subStageName;
              initialSubStageId = winSub.subStageId;
            }
          } else if (leadData.subStage && currentStage.subStages) {
            const currentSubStage = currentStage.subStages.find(
              s => (s.subStageName === leadData.subStage || (leadData.subStageConstant && s.subStageConstant?.toLowerCase() === leadData.subStageConstant?.toLowerCase())) && s.isActive
            );

            if (currentSubStage) {
              initialSubStage = currentSubStage.subStageName;
              initialSubStageId = currentSubStage.subStageId;
            }
          }

          setFormData(prev => ({
            ...prev,
            leadStage: currentStage.stageName,
            leadStageId: currentStage.stageId,
            subStage: initialSubStage,
            subStageId: initialSubStageId,
          }));
        }
      }
    }
  }, [stages, leadData, isOpen]);

  // Fetch Custom Fields for the selected SubStage
  const loadCustomFields = useCallback(async () => {
    if (!formData.subStageId) {
      setCustomFields([]);
      setFieldValues({});
      return;
    }
    setFieldsLoading(true);
    try {
      let list = [];
      const isCurrentSubStage = leadData?.id && (
        (formData.subStage === leadData.subStage) ||
        (!formData.subStage && !leadData.subStage)
      );

      if (isCurrentSubStage) {
        // Fetch current stage fields along with their filled values for this lead
        const res = await getLeadFieldValues(leadData.id);
        let raw = res?.data ?? res;
        if (typeof raw === "string") {
          try {
            raw = JSON.parse(raw);
          } catch (e) {
            console.error("Failed to parse lead field values JSON string", e);
          }
        }
        list = Array.isArray(raw) ? raw : [];
      } else {
        // Fetch empty stage field definitions for a different substage
        const res = await getStageFieldsBySubStageId(formData.subStageId);
        let raw = res?.data ?? res;
        if (typeof raw === "string") {
          try {
            raw = JSON.parse(raw);
          } catch (e) {
            console.error("Failed to parse stage fields JSON string", e);
          }
        }
        list = Array.isArray(raw) ? raw : [];
      }

      const activeFields = list.filter(f => f.IsActive || f.isActive);

      const mapped = activeFields.map(f => ({
        ...f,
        stageFieldId: f.stageFieldId ?? f.StageFieldId,
        fieldLabel: f.fieldLabel ?? f.FieldLabel,
        fieldTypeId: f.fieldTypeId ?? f.FieldTypeId,
        placeholder: f.placeholder ?? f.Placeholder,
        options: typeof (f.Options ?? f.options) === "string"
          ? JSON.parse(f.Options ?? f.options ?? "[]")
          : (f.Options ?? f.options ?? []),
        value: f.value ?? f.Value ?? "",
      }));
      setCustomFields(mapped);

      // Preserve existing field values or initialize, patching with saved values if they exist only if not already set
      setFieldValues(prev => {
        const nextValues = { ...prev };
        mapped.forEach(f => {
          if (nextValues[f.stageFieldId] === undefined) {
            nextValues[f.stageFieldId] = f.value ?? "";
          }
        });
        return nextValues;
      });

      // Initialize initial values for change tracking
      const initialVals = {};
      mapped.forEach(f => {
        initialVals[f.stageFieldId] = f.value ?? "";
      });
      setInitialFieldValues(initialVals);
    } catch (err) {
      console.error("Failed to load custom fields for substage", err);
    } finally {
      setFieldsLoading(false);
    }
  }, [formData.subStageId, leadData?.id, leadData?.subStageId]);

  useEffect(() => {
    loadCustomFields();
  }, [loadCustomFields]);

  // Clear field values when substage changes to avoid carryover
  useEffect(() => {
    setFieldValues({});
  }, [formData.subStageId]);

  // Option management inside the Stage Change Modal
  const handleCreateOption = async (stageFieldId, optionValue) => {
    try {
      const optionId = crypto.randomUUID();
      const field = customFields.find(f => f.stageFieldId === stageFieldId);
      const res = await upsertStageFieldOption({
        stageFieldOptionId: optionId,
        stageFieldId,
        optionValue,
        optionOrder: field ? (field.options || []).length : 0,
      });
      if (res?.statusCode === 200 || res?.status === "Success") {
        toast.success("Option created successfully");

        // Auto-select the newly created option in the state by its UUID (optionId)
        setFieldValues((prev) => ({
          ...prev,
          [stageFieldId]: optionId,
        }));

        await loadCustomFields(); // Reload fields to update UI options
      } else {
        toast.error(res?.message || "Failed to create option");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to create option");
    }
  };

  const handleEditOption = async (stageFieldId, optionId, oldLabel, newLabel) => {
    try {
      const field = customFields.find(f => f.stageFieldId === stageFieldId);
      const opt = field ? (field.options || []).find(o => (o.stageFieldOptionId ?? o.StageFieldOptionId) === optionId) : null;
      const res = await upsertStageFieldOption({
        stageFieldOptionId: optionId,
        stageFieldId,
        optionValue: newLabel,
        optionOrder: opt ? (opt.optionOrder ?? opt.OptionOrder ?? 0) : 0,
      });
      if (res?.statusCode === 200 || res?.status === "Success") {
        toast.success("Option updated successfully");
        await loadCustomFields();
      } else {
        toast.error(res?.message || "Failed to update option");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to update option");
    }
  };

  const handleDeleteOption = async (stageFieldId, optionId) => {
    try {
      const res = await deleteStageFieldOption(optionId, stageFieldId);
      if (res?.statusCode === 200 || res?.status === "Success") {
        toast.success("Option deleted successfully");
        await loadCustomFields();
      } else {
        toast.error(res?.message || "Failed to delete option");
      }
    } catch (err) {
      toast.error(err?.message || "Failed to delete option");
    }
  };

  const resetForm = () => {
    setFormData({
      leadStage: "",
      leadStageId: "",
      subStage: "",
      subStageId: "",
      notes: (leadData?.remarks && !leadData.remarks.toLowerCase().includes("reverted")) ? leadData.remarks : "",
      leadScore: "",
    });
    setCustomFields([]);
    setFieldValues({});
    setInitialFieldValues({});
    setIsSubmitting(false);
  };
  const loadStages = async () => {
    try {
      setLoading(true);
      const stagesData = await getStageAndSubStages({ pageSize: 100 });
      let activeSortedStages = (stagesData || [])
        .filter(s => s?.isActive)
        .sort((a, b) => (a.stageOrder ?? 0) - (b.stageOrder ?? 0));

      const isBulk = Boolean(leadData?.isBulk || Array.isArray(leadData?.id));
      const savedStageConstant = leadData?.stageConstant || leadData?.StageConstant || leadData?.Stage?.stageConstant || leadData?.Stage?.StageConstant || "";
      const isSavedPostContract = isJobStage(savedStageConstant);
      if (isBulk && !isSavedPostContract) {
        activeSortedStages = activeSortedStages.filter(
          s => !isJobStage(s.stageConstant)
        );
      }
      setStages(activeSortedStages);
    } catch (error) {
      console.error("Error loading stages:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleStageChange = (stage) => {
    if (stage) {
      const isContractWonLost = isClosedStage(stage.stageConstant);
      let defaultSubStage = "";
      let defaultSubStageId = "";

      if (isContractWonLost && stage.subStages) {
        const winSubStage = stage.subStages.find(
          sub => sub.isActive && (
            sub.subStageName?.toLowerCase().includes("win") ||
            sub.subStageName?.toLowerCase().includes("won") ||
            sub.subStageConstant?.toLowerCase() === "win" ||
            sub.subStageConstant?.toLowerCase() === "won"
          )
        );
        if (winSubStage) {
          defaultSubStage = winSubStage.subStageName;
          defaultSubStageId = winSubStage.subStageId;
        }
      }

      setFormData(prev => ({
        ...prev,
        leadStage: stage.stageName,
        leadStageId: stage.stageId,
        subStage: defaultSubStage,
        subStageId: defaultSubStageId,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        leadStage: "",
        leadStageId: "",
        subStage: "",
        subStageId: "",
      }));
    }
  };

  // Ensure Win subStage is auto-selected when stage is Contract Won/Lost
  useEffect(() => {
    if (formData.leadStageId && stages.length > 0) {
      const currentStageObj = stages.find(s => s.stageId === formData.leadStageId);
      if (currentStageObj && isClosedStage(currentStageObj.stageConstant)) {
        const winSubStage = currentStageObj.subStages?.find(
          s => s.isActive && (
            s.subStageName?.toLowerCase().includes("win") ||
            s.subStageName?.toLowerCase().includes("won") ||
            s.subStageConstant?.toLowerCase() === "win" ||
            s.subStageConstant?.toLowerCase() === "won"
          )
        );
        if (winSubStage && formData.subStageId !== winSubStage.subStageId) {
          setFormData(prev => ({
            ...prev,
            subStage: winSubStage.subStageName,
            subStageId: winSubStage.subStageId,
          }));
        }
      }
    }
  }, [formData.leadStageId, stages]);

  const handleSubStageChange = (subStage) => {
    if (subStage) {
      setFormData(prev => ({
        ...prev,
        subStage: subStage.subStageName,
        subStageId: subStage.subStageId,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        subStage: "",
        subStageId: "",
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.leadStage || !formData.leadStageId) {
      toast.info("Please select a lead stage");
      return;
    }

    // Map fields state values to the List<LeadFieldValueRequest> format, sending only filled fields
    const fieldValuesList = Object.entries(fieldValues)
      .filter(([_, val]) => val !== null && val !== undefined && String(val).trim() !== "")
      .map(([stageFieldId, val]) => ({
        leadId: leadData?.id,
        stageFieldId,
        value: val
      }));

    const stageChanged = (formData.leadStage || "") !== (leadData?.stage || "");
    const subStageChanged = (formData.subStage || "") !== (leadData?.subStage || "");

    // Compare current field values against initial field values to detect actual changes
    const customFieldsChanged = Object.keys(initialFieldValues).some(
      (key) => (fieldValues[key] ?? "") !== (initialFieldValues[key] ?? "")
    );

    if (!stageChanged && !subStageChanged && !customFieldsChanged) {
      toast.info("No changes detected. Please select a different stage, substage, or modify a custom field.");
      return;
    }

    setIsSubmitting(true);
    try {
      const selectedStage = stages.find(s => s.stageId === formData.leadStageId) || null;
      const currentStageSubStages = selectedStage?.subStages?.filter(sub => sub.isActive) || [];
      const selectedSubStage = currentStageSubStages.find(s => s.subStageId === formData.subStageId) || null;

      const result = await onStageChange({
        ...formData,
        stageId: formData.leadStageId,
        subStageId: formData.subStageId || null,
        stageConstant: selectedStage?.stageConstant || null,
        subStageConstant: selectedSubStage?.subStageConstant || null,
        leadId: leadData?.id,
        leadScore: formData.leadScore ? parseInt(formData.leadScore, 10) : null,
        fieldValues: fieldValuesList, // Include the custom fields in submit data
      });

      if (result !== false) {
        onClose();
      }
    } catch (error) {
      console.error("Error updating stage:", error);
      toast.error("Failed to update stage");
    } finally {
      setIsSubmitting(false);
    }
  };

  const savedStageConstant = leadData?.stageConstant || leadData?.StageConstant || leadData?.Stage?.stageConstant || leadData?.Stage?.StageConstant || "";

  const currentSelectedStageObj = stages.find(s => s.stageId === formData.leadStageId);
  const currentSelectedStageName = formData?.leadStage || currentSelectedStageObj?.stageName || "";
  const currentSelectedStageConstant = currentSelectedStageObj?.stageConstant || "";

  const isSavedStagePostContract = isJobStage(savedStageConstant);

  // A converted lead's stage is no longer locked to just the 3 job stages -
  // it can move to any stage; leaving a job stage warns the caller (see
  // shouldWarnJobCancellation in the pages that render this modal) and
  // cancels the associated Job server-side.
  const availableLeadStages = useMemo(() => {
    const isBulk = Boolean(leadData?.isBulk || Array.isArray(leadData?.id));
    if (isBulk && !isSavedStagePostContract) {
      return stages.filter(s => !isJobStage(s.stageConstant));
    }
    return stages;
  }, [stages, isSavedStagePostContract, leadData]);

  const currentStage = stages.find(s => s.stageId === formData.leadStageId);
  const isContractWonLost = currentStage && isClosedStage(currentStage.stageConstant);

  let availableSubStages = currentStage?.subStages?.filter(sub => sub.isActive) || [];
  if (isContractWonLost) {
    availableSubStages = availableSubStages.filter(
      sub => !sub.subStageName?.toLowerCase().includes("lost")
    );
  }

  const selectedStage = stages.find(s => s.stageId === formData.leadStageId) || null;
  const selectedSubStage = availableSubStages.find(s => s.subStageId === formData.subStageId) || null;

  const displayLeadName =
    (leadData?.title && !["unknown", "unknown lead", "unknown user", "n/a", "-", "null", "undefined"].includes(String(leadData.title).trim().toLowerCase()) ? String(leadData.title).trim() : null) ||
    (leadData?.name && !["unknown", "unknown lead", "unknown user", "n/a", "-", "null", "undefined"].includes(String(leadData.name).trim().toLowerCase()) ? String(leadData.name).trim() : null) ||
    (leadData?.FullName && !["unknown", "unknown lead", "unknown user", "n/a", "-", "null", "undefined"].includes(String(leadData.FullName).trim().toLowerCase()) ? String(leadData.FullName).trim() : null) ||
    leadData?.LeadRawJsonData?.find(item => item?.MasterFieldName?.toLowerCase() === "title")?.Value ||
    null;

  return (
    <Transition appear show={isOpen} as={Fragment}>
      <Dialog
        as="div"
        className="fixed inset-0 z-[100] flex flex-col items-center justify-center overflow-hidden px-4 py-6 sm:px-5"
        onClose={() => {}}
      >
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="absolute inset-0 bg-gray-900/50 backdrop-blur transition-opacity dark:bg-black/30" />
        </TransitionChild>

        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0 scale-95"
          enterTo="opacity-100 scale-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100 scale-100"
          leaveTo="opacity-0 scale-95"
        >
          <DialogPanel className="relative flex w-full max-w-lg origin-top flex-col overflow-hidden rounded-lg bg-white transition-all duration-300 dark:bg-dark-700 outline-none focus:outline-none">
            <div className="flex items-center justify-between rounded-t-lg bg-gray-200 px-4 py-3 dark:bg-dark-800 sm:px-5">
              <DialogTitle
                as="h3"
                className="flex items-center gap-2 text-base font-medium text-gray-800 dark:text-dark-100"
              >
                <CheckCircleIcon className="h-5 w-5 text-blue-600" />
                Change Lead Stage
              </DialogTitle>
              <Button
                type="button"
                onClick={(e) => {
                  e?.preventDefault?.();
                  e?.stopPropagation?.();
                  if (onClose) onClose();
                }}
                variant="flat"
                isIcon
                className="size-7 rounded-full ltr:-mr-1.5 rtl:-ml-1.5 cursor-pointer"
              >
                <XMarkIcon className="size-4.5" />
              </Button>
            </div>

            <div className="flex flex-col overflow-y-auto px-4 py-4 sm:px-5">
              {displayLeadName && (
                <div className="mb-4 rounded-lg bg-gray-50 px-3 py-2 border border-gray-100 dark:bg-dark-800 dark:border-dark-600">
                  <span className="text-[11px] font-semibold text-gray-500 dark:text-dark-300 uppercase tracking-wider">Lead: </span>
                  <span className="text-xs font-bold text-gray-900 dark:text-white">{displayLeadName}</span>
                </div>
              )}

              <div className="space-y-5">
                {/* Lead Stage */}
                <Listbox
                  label="Lead Stage *"
                  data={availableLeadStages}
                  value={selectedStage}
                  onChange={handleStageChange}
                  displayField="stageName"
                  placeholder="Select Lead Stage"
                  disabled={loading || isSubmitting}
                />

                {/* Sub Stage */}
                <Listbox
                  label="Sub Stage (Optional)"
                  data={availableSubStages.sort((a, b) => a.subStageOrder - b.subStageOrder)}
                  value={selectedSubStage}
                  onChange={handleSubStageChange}
                  displayField="subStageName"
                  placeholder={
                    !formData.leadStageId
                      ? "Select a stage first"
                      : availableSubStages.length === 0
                        ? "No sub-stages available"
                        : "Select Sub Stage (Optional)"
                  }
                  disabled={isSubmitting || !formData.leadStageId || availableSubStages.length === 0 || isContractWonLost}
                />

                {/* Dynamic Custom Fields */}
                {fieldsLoading ? (
                  <div className="text-xs text-gray-500 italic dark:text-gray-400">Loading custom fields...</div>
                ) : (
                  customFields.map((field) => {
                    const value = fieldValues[field.stageFieldId] || "";
                    const isDropdown = String(field.fieldTypeId) === String(dropdownTypeId);

                    return (
                      <div key={field.stageFieldId} className="space-y-1">
                        <label className="text-xs font-medium text-gray-600 dark:text-gray-400">
                          {field.fieldLabel}
                        </label>
                        {isDropdown ? (
                          <CustomCreatableDropdown
                            options={(field.options ?? []).map((o) => ({
                              value: String(o.stageFieldOptionId ?? o.StageFieldOptionId).toLowerCase(),
                              label: o.optionValue ?? o.OptionValue,
                            }))}
                            value={(() => {
                              if (!value) return null;
                              const matchingOption = (field.options ?? []).find(
                                (o) => String(o.stageFieldOptionId ?? o.StageFieldOptionId).toLowerCase() === String(value).toLowerCase()
                              );
                              return {
                                value: value,
                                label: matchingOption ? (matchingOption.optionValue ?? matchingOption.OptionValue) : value,
                              };
                            })()}
                            onChange={(selected) =>
                              setFieldValues((prev) => ({
                                ...prev,
                                [field.stageFieldId]: selected?.value || "",
                              }))
                            }
                            placeholder={field.placeholder || "Select option..."}
                            addNewText="+ Add New Option"
                            menuHeader="Dropdown Options"
                            onCreateOption={(val) => handleCreateOption(field.stageFieldId, val)}
                            onEditOption={(id, oldLabel, newLabel) =>
                              handleEditOption(field.stageFieldId, id, oldLabel, newLabel)
                            }
                            onDeleteOption={(id) => handleDeleteOption(field.stageFieldId, id)}
                            disabled={isSubmitting}
                          />
                        ) : (
                          <Input
                            placeholder={field.placeholder || "Enter value..."}
                            value={value}
                            onChange={(e) => setFieldValues(prev => ({ ...prev, [field.stageFieldId]: e.target.value }))}
                            className="text-sm"
                            disabled={isSubmitting}
                          />
                        )}
                      </div>
                    );
                  })
                )}

                {/* Notes */}
                <Textarea
                  label="Notes (Optional)"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  placeholder="Add notes about this stage change..."
                  rows={3}
                  disabled={isSubmitting}
                />
              </div>

              <div className="mt-6 space-x-3 text-end">
                <Button
                  onClick={onClose}
                  variant="outlined"
                  className="min-w-[7rem] rounded-full"
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleSubmit}
                  color="primary"
                  ref={saveRef}
                  className="min-w-[7rem] rounded-full"
                  disabled={loading || isSubmitting || !formData.leadStage}
                >
                  {isSubmitting ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            </div>
          </DialogPanel>
        </TransitionChild>
      </Dialog>
    </Transition>
  );
};

export default StageChangeModal;
